import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import Logo from "./Logo";
import { SITE } from "../data/site";

const LINKS = [
  { to: "/", label: "Home", id: "nav-home-link" },
  { to: "/about", label: "About", id: "nav-about-link" },
  { to: "/products", label: "Products", id: "nav-products-link" },
  { to: "/industries", label: "Industries", id: "nav-industries-link" },
  { to: "/blog", label: "Blog", id: "nav-blog-link" },
  { to: "/enquiry", label: "Enquiry", id: "nav-enquiry-link" },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 border-b transition-colors duration-300 ${
        scrolled || open
          ? "bg-white/90 backdrop-blur-md border-slate-200"
          : "bg-transparent border-transparent"
      }`}
      data-testid="site-navbar"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3 sm:px-8">
        <Link to="/" data-testid="nav-logo-link" aria-label="Universal Engineers — home">
          <Logo dark={!scrolled && !open} />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {LINKS.slice(0, 5).map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.id}
              className={({ isActive }) =>
                `font-mono text-[11px] uppercase tracking-[0.2em] transition-colors ${
                  isActive ? "text-safety" : "text-slate-600 hover:text-navy"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <a
            href={SITE.phoneHref}
            data-testid="nav-phone-link"
            className="hidden xl:flex items-center gap-2 font-mono text-xs text-navy hover:text-safety transition-colors"
          >
            <Phone size={14} className="text-safety" />
            {SITE.phoneDisplay}
          </a>
          <Link
            to="/enquiry"
            data-testid="nav-quick-rfq-button"
            className="group relative overflow-hidden bg-safety px-5 py-2.5 font-display text-xs font-bold uppercase tracking-[0.14em] text-white transition-transform hover:-translate-y-0.5"
          >
            <span className="relative z-10">Quick RFQ</span>
            <span className="absolute inset-0 -translate-x-full bg-navy transition-transform duration-300 group-hover:translate-x-0" />
          </Link>
        </nav>

        <button
          className="lg:hidden p-2 text-navy"
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          aria-label={open ? "Close menu" : "Open menu"}
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden border-t border-slate-200 bg-white/95 backdrop-blur-md"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col px-6 py-4">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -14 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={l.id}
                    className={({ isActive }) =>
                      `block border-b border-slate-100 py-4 font-display text-xl font-bold tracking-tight ${
                        isActive ? "text-safety" : "text-navy"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <a
                href={SITE.phoneHref}
                data-testid="nav-mobile-phone-link"
                className="flex items-center gap-2 py-4 font-mono text-sm text-navy"
              >
                <Phone size={16} className="text-safety" /> {SITE.phoneDisplay}
              </a>
              <Link
                to="/enquiry"
                data-testid="nav-mobile-rfq-button"
                className="mt-2 mb-4 bg-safety px-5 py-3 text-center font-display text-sm font-bold uppercase tracking-[0.14em] text-white"
              >
                Quick RFQ
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
